import React, { useState } from 'react';
import { ArrowLeft, GitBranch, Layers, ArrowRightCircle, MousePointer, Lightbulb } from 'lucide-react';

const BuildingInteractiveFlowsArticle = ({ onBack }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button 
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-black mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Help Center
      </button>

      <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-lg mb-8">
        <div className="flex items-start">
          <GitBranch className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-bold mb-3">Building Interactive Flows</h1>
            <p className="text-gray-700">
              Learn how to combine multiple views into cohesive user journeys that simulate realistic interactions. 
              This guide will show you how to create consistent experiences across screens and test user flows 
              before implementation.
            </p>
          </div>
        </div>
      </div>

      {!expanded ? (
        <div>
          <p className="text-gray-700 mb-6">
            Individual views are powerful, but connecting them into flows creates a complete interactive 
            experience. With flows, you can demonstrate user journeys, test navigation patterns, and 
            present stakeholders with a more complete picture of your product's functionality.
          </p>
          <button
            onClick={() => setExpanded(true)}
            className="text-black font-medium hover:underline"
          >
            Read more
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Layers className="w-5 h-5 mr-2 text-gray-700" />
              Understanding Flows
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                A flow is a sequence of views that represent a user journey through your product. For example:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Onboarding flow</strong>: Welcome screen → Sign up → Profile creation → Dashboard</li>
                <li><strong>Checkout flow</strong>: Cart → Shipping details → Payment → Confirmation</li>
                <li><strong>Content creation flow</strong>: Create new → Edit details → Preview → Publish</li>
              </ul>
              <p className="mt-4">
                By organizing your views into flows, you create a more comprehensive prototype that demonstrates 
                not just how individual screens look, but how users move between them to accomplish tasks.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <ArrowRightCircle className="w-5 h-5 mr-2 text-gray-700" />
              Creating Your First Flow
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                To create a flow, you'll need to have already generated at least two views. Here's how to connect them:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Navigate to your project dashboard</strong> and click "+ New" in the Flows section.
                </li>
                <li>
                  <strong>Give your flow a descriptive title</strong> that indicates its purpose (e.g., "User Registration Flow").
                </li>
                <li>
                  <strong>Add views to your flow</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Drag views from the left panel to the flow panel on the right.</li>
                    <li>Select the specific version of each view you want to include.</li>
                    <li>Arrange them in the order users would naturally navigate through them.</li>
                  </ul>
                </li>
                <li>
                  <strong>Reorder views if needed</strong> using the up and down arrows to match your intended sequence.
                </li>
                <li>
                  <strong>Save your flow</strong> when you're satisfied with the sequence.
                </li>
              </ol>
              <p className="mt-4">
                Once created, you can view the entire flow as a presentation, stepping through each screen to simulate the user experience.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <MousePointer className="w-5 h-5 mr-2 text-gray-700" />
              Best Practices for Effective Flows
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Creating effective flows requires thoughtful planning and attention to detail:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Maintain visual consistency</strong> - Ensure elements like headers, navigation, and buttons look and behave consistently across views.
                </li>
                <li>
                  <strong>Follow a logical sequence</strong> - Arrange views in the order users would naturally progress through tasks.
                </li>
                <li>
                  <strong>Include all possible paths</strong> - Consider successful journeys as well as error states and alternative paths.
                </li>
                <li>
                  <strong>Use the latest versions</strong> - When updating views, make sure your flows incorporate the most recent versions.
                </li>
                <li>
                  <strong>Keep flows focused</strong> - Each flow should represent a complete but specific user journey rather than trying to show everything.
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <GitBranch className="w-5 h-5 mr-2 text-gray-700" />
              Managing Multiple Flows
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                As your project grows, you may create multiple flows to represent different user journeys:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Create separate flows for distinct user types</strong> - For example, "New User Journey" vs. "Returning User Journey".
                </li>
                <li>
                  <strong>Develop flows for specific features</strong> - Break down complex products into feature-specific flows.
                </li>
                <li>
                  <strong>Create alternative flows</strong> - Experiment with different approaches to the same journey to compare options.
                </li>
                <li>
                  <strong>Label flows clearly</strong> - Use descriptive names that indicate purpose and user type.
                </li>
                <li>
                  <strong>Reuse views across flows</strong> - The same view can appear in multiple flows if relevant.
                </li>
              </ul>
              <p className="mt-4">
                Well-organized flows make it easier to demonstrate your product to stakeholders and get meaningful feedback on the entire user experience.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              Tips for Presenting Flows
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                When sharing your flows with stakeholders or team members:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Provide context</strong> - Begin by explaining the user's goal and starting point.</li>
                <li><strong>Narrate the journey</strong> - As you navigate through screens, explain what the user is doing and why.</li>
                <li><strong>Highlight key interactions</strong> - Point out important interactive elements on each screen.</li>
                <li><strong>Show transitions</strong> - Explain how users move from one screen to another.</li>
                <li><strong>Gather focused feedback</strong> - Ask specific questions about usability and clarity rather than just general impressions.</li>
              </ul>
            </div>
          </section>

          <button
            onClick={() => setExpanded(false)}
            className="text-black font-medium hover:underline mt-4 block"
          >
            Show less
          </button>
        </div>
      )}
    </div>
  );
};

export default BuildingInteractiveFlowsArticle;